import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import API, { createAxiosHeaders } from '../utils/api';
import payApi from '../utils/payApi';
import Payment from './Payment';

const CssTextField = styled(TextField)({
  '& label': { color: '#FFF' },
  '& .MuiFormHelperText-root': { color: '#FFF' },
  '& label.Mui-focused': { color: '#A0AAB4' },
  '& .MuiInputBase-root': { color: '#FFF', textAlign: 'center' },
  '& .MuiSelect-select': { color: '#FFF', textAlign: 'center' },
});

const TeamManager = ({ userData, leagueData }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [leagueName, setLeagueName] = useState(leagueData?.name || '');
  const [teamMembers, setTeamMembers] = useState([]);
  const [pricePerMember, setPricePerMember] = useState(leagueData?.registration_fee ?? null);
  const [transactions, setTransactions] = useState([]);
  const [newMember, setNewMember] = useState({
    firstname: '',
    lastname: '',
    email: '',
    expert: '',
    personalcode: '',
    mobile: '',
    leader: userData.email,
  });

  const headers = createAxiosHeaders();

  // Fetch payment transactions
  useEffect(() => {
    if (!userData) return;
    payApi.getPaymentStatus()
      .then(res => setTransactions(res.data.transactions))
      .catch(err => {
        console.error(err);
        enqueueSnackbar('خطا در بررسی وضعیت پرداخت.', {
          variant: 'error',
          action: key => (
            <button onClick={() => closeSnackbar(key)} className="text-white">
              بستن
            </button>
          ),
        });
      });
  }, [userData, enqueueSnackbar, closeSnackbar]);

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const res = await API.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-members/get/${userData.email}`,
        headers
      );
      setTeamMembers(res.data);
    } catch (error) {
      enqueueSnackbar('عدم موفقیت در بارگذاری اعضای تیم', {
        variant: 'error',
        action: key => (
          <IconButton onClick={() => closeSnackbar(key)}>
            <CloseIcon style={{ color: '#FFF' }} />
          </IconButton>
        ),
      });
    }
  };

  // Fetch league name
  const fetchLeagueName = async () => {
    if (leagueData) {
      setLeagueName(leagueData.name);
      setPricePerMember(leagueData.registration_fee);
      return;
    }
    try {
      const res = await API.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/leagues/${userData.league}/`,
        headers
      );
      setLeagueName(res.data.name);
      setPricePerMember(res.data.registration_fee)
    } catch (error) {
      console.error('Error fetching league name:', error);
    }
  };

  useEffect(() => {
    if (!userData) return;
    fetchTeamMembers();
    fetchLeagueName();
  }, [userData, leagueData]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewMember(prev => ({ ...prev, [name]: value }));
  };

  const handleAddMember = async () => {
    const { firstname, lastname, email, personalcode, mobile } = newMember;
    if (!firstname || !lastname || !email || !personalcode || !mobile) {
      enqueueSnackbar('تمام فیلدها باید تکمیل شوند', { variant: 'error' });
      return;
    }
   
    try {
      await API.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-members/`,
        newMember,
        headers
      );
      setNewMember({ firstname: '', lastname: '', email: '', expert: '', personalcode: '', mobile: '', leader: userData.email });
      await fetchTeamMembers();
      enqueueSnackbar('با موفقیت افزوده شد', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('عدم موفقیت در افزودن عضو تیم', { variant: 'error' });
    }
  };

  const handleEditMember = async (id, updatedData) => {
    try {
      await API.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-members/${id}/`,
        updatedData,
        headers
      );
      await fetchTeamMembers();
      enqueueSnackbar('تغییرات ذخیره شد', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('خطا در به‌روزرسانی عضو', { variant: 'error' });
    }
  };

  return (
    <div className="flex flex-col text-white" dir="rtl">
      <h2>فرم ثبت‌نام در لیگ {leagueName} کارون کاپ</h2>
      <div>هزینه ثبت نام هر عضو {pricePerMember} تومان می باشد</div>

      {userData.is_preverified == false ? (
        <section>
          <h3>افزودن عضو جدید</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {['firstname', 'lastname', 'expert', 'personalcode', 'mobile', 'email'].map(field => (
              <CssTextField
                key={field}
                name={field}
                label={
                  field === 'expert'
                    ? 'مسئولیت در تیم'
                    : field === 'personalcode'
                    ? 'کد ملی'
                    : field === 'mobile'
                    ? 'شماره موبایل'
                    : field === 'email'
                    ? 'ایمیل'
                    : field === 'firstname'
                    ? 'نام'
                    : 'نام خانوادگی'
                }
                type={field === 'email' ? 'email' : 'text'}
                value={newMember[field]}
                onChange={handleInputChange}
                variant="standard"
              />
            ))}
          </div>
          <button
            onClick={handleAddMember}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700 mt-4"
          >
            اضافه کردن
          </button>
        </section>
      ) : (
        <div className="mt-6">
          <h3>افزایش ظرفیت تیم</h3>
          <Payment
            pricePerMember={pricePerMember}
            teamMembersCount={teamMembers.length}
            transactions={transactions}
          />
        </div>
      )}

      <section className="mt-8">
        <h3>اعضای تیم</h3>
        <ul className="space-y-2">
          {teamMembers.map(member => (
            <li key={member.id} className="flex justify-between items-center">
              <span>
                {member.firstname} {member.lastname} - {member.expert}
              </span>
              <button
                onClick={() => handleEditMember(member.id, { ...member })}
                className="text-sm text-indigo-300 hover:underline"
              >
                ویرایش
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default TeamManager;
